"use client";

import { useState } from "react";
import { BlobImage } from "@/components/BlobImage";

// Checkerboard behind see-through pixels (each side needs its own).
const checker =
  "bg-[#e6e9f2] [background-image:conic-gradient(#e2dbcf_25%,transparent_0_50%,#e2dbcf_0_75%,transparent_0)] [background-size:20px_20px]";

/** Drag the handle (or use arrow keys) to compare the original with the
 *  result (FR-25). Both are shown at the same size so quality is comparable. */
export function BeforeAfter({
  before,
  after,
  dim = false,
}: {
  before: Blob;
  after: Blob;
  /** Fade the result while a new preview is being made. */
  dim?: boolean;
}) {
  const [split, setSplit] = useState(50);
  const [beforeBroken, setBeforeBroken] = useState(false);
  const label =
    "absolute top-3 rounded-md bg-ink/80 px-2.5 py-1 text-[13px] font-semibold text-white";

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`relative aspect-[4/3] overflow-hidden rounded-[10px] ${checker}`}
      >
        <BlobImage
          blob={after}
          alt="Result"
          className={`absolute inset-0 size-full object-contain transition-opacity ${dim ? "opacity-60" : ""}`}
        />
        {!beforeBroken && (
          <div
            className={`absolute inset-0 ${checker}`}
            style={{ clipPath: `inset(0 ${100 - split}% 0 0)` }}
          >
            <BlobImage
              blob={before}
              alt="Original"
              onError={() => setBeforeBroken(true)}
              className="absolute inset-0 size-full object-contain"
            />
          </div>
        )}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 w-0.5 bg-white"
          style={{ left: `${split}%` }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 flex size-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white font-semibold text-ink shadow"
          style={{ left: `${split}%` }}
        >
          ⇆
        </span>
        <span className={`${label} left-3`}>Before</span>
        <span className={`${label} right-3`}>After</span>
        <input
          type="range"
          min={0}
          max={100}
          value={split}
          onChange={(e) => setSplit(Number(e.target.value))}
          aria-label="Compare before and after"
          className="absolute inset-0 size-full cursor-ew-resize opacity-0"
        />
      </div>
    </div>
  );
}
